// ─────────────────────────────────────────────────────────────────────────────
// RF-002 — ACTIVACIÓN de cuenta Frontline (/app/activar?token=). El colaborador cargado por el padrón
// (carga masiva) llega desde el enlace del correo de bienvenida: FIJA su contraseña y acepta los
// legales; la cuenta queda lista para el login. Consume POST v1/users/frontline-activate.
// Estilo y consentimiento a la par del registro (clases .acc-*, 2 políticas con modal scroll-to-accept).
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFrontlineActivate, useLegalDocument, getRecognitionErrorMessage } from '@/api/recognition';
import type { FrontlineActivateBody, LegalDocType } from '@/api/recognition';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { UserProvider } from '@/contexts/UserContext';
import { LanguageSelector, AuthLogo, AuthField, AuthHelp, RegisterPolicyModal } from './accessShared';
import '../recognition.css';
import { withBase } from '@/utils/basePath';

function ActivateForm({ token }: Readonly<{ token: string }>) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language?.split('-')[0] ?? 'es';
  const activate = useFrontlineActivate();
  // Versión legal vigente servida por el backend (trazabilidad ARCO), igual que el registro. SIN
  // `?? '1.0'`: sin versión resuelta no se registra la aceptación, porque sería una versión
  // inventada sin ningún documento detrás.
  //
  // Aquí NO se pasa `ouId`: esta pantalla solo tiene el token del correo y no resuelve la OU de la
  // persona, así que el servidor resuelve el documento sin filtrar por OU (que es la misma regla
  // que en el registro antes de elegir organización).
  const privacyDoc = useLegalDocument('privacy', lang).data;
  const legalDoc = useLegalDocument('legal', lang).data;
  // La COMPARABLE (la del texto publicado) es la que se firma: es la que las compuertas comparan.
  const privacyVersion = privacyDoc?.comparableVersion ?? privacyDoc?.version;
  const legalVersion = legalDoc?.comparableVersion ?? legalDoc?.version;

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptLegal, setAcceptLegal] = useState(false);
  const [policy, setPolicy] = useState<LegalDocType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { setError(t('activate.errPassword', 'La contraseña debe tener al menos 8 caracteres.')); return; }
    if (password !== confirm) { setError(t('activate.errPasswordMatch', 'Las contraseñas no coinciden.')); return; }
    if (!acceptPrivacy || !acceptLegal) { setError(t('activate.errLegal', 'Debes aceptar el Aviso de Privacidad y las Bases Legales.')); return; }
    if (!privacyVersion || !legalVersion) {
      setError(t('register.errLegalVersion', 'No se pudo cargar el documento legal. Vuelve a intentarlo.'));
      return;
    }
    setError(null);
    const now = new Date().toISOString();
    const body: FrontlineActivateBody = {
      token,
      password,
      // Clave CANÓNICA + evidencia de lo mostrado, igual que el registro (por qué antes eran otras,
      // en `LEGAL_LEGACY_SIGNUP_KEYS`).
      aceptaciones: [
        {
          documentKey: 'privacy', version: privacyVersion, acceptedAt: now, source: 'frontline-activate',
          displayedVersion: privacyDoc?.contentSource === 'pdf' ? privacyDoc.version : null,
        },
        {
          documentKey: 'legal', version: legalVersion, acceptedAt: now, source: 'frontline-activate',
          displayedVersion: legalDoc?.contentSource === 'pdf' ? legalDoc.version : null,
        },
      ],
    };
    activate.mutate(body);
  };

  if (activate.isSuccess) {
    return (
      <div className="acc-success" role="status">
        <div className="acc-success-icon">✅</div>
        <h2>{t('activate.doneTitle', 'Cuenta activada')}</h2>
        <p>{t('activate.doneSub', 'Ya puedes iniciar sesión con tu correo y la contraseña que creaste.')}</p>
        <a className="acc-submit acc-submit--link" href={withBase('/login')}>
          {t('login.submit', 'Iniciar sesión')} <i className="fas fa-arrow-right" />
        </a>
      </div>
    );
  }

  return (
    <form className="acc-form" onSubmit={submit} noValidate>
      <AuthField icon="fa-lock" withEye value={password} onChange={setPassword} placeholder={t('activate.password', 'Crea tu contraseña (mín. 8 caracteres)')} autoComplete="new-password" />
      <AuthField icon="fa-lock" withEye value={confirm} onChange={setConfirm} placeholder={t('activate.confirmPassword', 'Confirmar contraseña')} autoComplete="new-password" />

      {/* Dos aceptaciones legales con modal scroll-to-accept (CPE-009), a la par del registro. */}
      <div className="acc-checkbox">
        <input
          id="act-priv"
          type="checkbox"
          checked={acceptPrivacy}
          onChange={(e) => { if (e.target.checked) setPolicy('privacy'); else setAcceptPrivacy(false); }}
        />
        <label htmlFor="act-priv">
          {t('register.acceptPrivacy', 'He leído y acepto el')}{' '}
          <button type="button" className="acc-policy-link" onClick={() => setPolicy('privacy')}>{t('register.privacyLink', 'Aviso de Privacidad')}</button>
        </label>
      </div>
      <div className="acc-checkbox">
        <input
          id="act-legal"
          type="checkbox"
          checked={acceptLegal}
          onChange={(e) => { if (e.target.checked) setPolicy('legal'); else setAcceptLegal(false); }}
        />
        <label htmlFor="act-legal">
          {t('register.acceptLegalBases', 'He leído y acepto las')}{' '}
          <button type="button" className="acc-policy-link" onClick={() => setPolicy('legal')}>{t('register.legalLink', 'Bases Legales')}</button>
        </label>
      </div>

      {error && <p className="rp-error" role="alert"><i className="fas fa-triangle-exclamation" /> {error}</p>}
      {activate.isError && <p className="rp-error" role="alert">{getRecognitionErrorMessage(activate.error, t('activate.submitError', 'No se pudo activar la cuenta. El enlace puede haber expirado.'))}</p>}

      <button type="submit" className="acc-submit" disabled={activate.isPending}>
        {activate.isPending ? t('admin.saving', 'Guardando…') : t('activate.submit', 'Activar mi cuenta')} <i className="fas fa-arrow-right" />
      </button>

      <AuthHelp />

      {policy && (
        <RegisterPolicyModal
          documentKey={policy}
          onClose={() => setPolicy(null)}
          onAccept={() => { if (policy === 'privacy') setAcceptPrivacy(true); else setAcceptLegal(true); }}
        />
      )}
    </form>
  );
}

function ActivateContent() {
  const { t } = useTranslation('recognition');
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token'), [params]);

  return (
    <div className="acc-page">
      <div className="acc-topbar"><LanguageSelector /></div>
      <div className="acc-card">
        <AuthLogo />
        <h1 className="acc-title">{t('activate.title', 'Activa tu cuenta')}</h1>
        <p className="acc-subtitle">{t('activate.subtitle', 'Crea tu contraseña para entrar al Programa de Reconocimiento.')}</p>
        {token
          ? <ActivateForm token={token} />
          : (
            <div className="acc-success" role="alert">
              <div className="acc-success-icon">⚠️</div>
              <h2>{t('activate.noToken', 'Enlace inválido')}</h2>
              <p>{t('activate.noTokenSub', 'El enlace de activación no es válido o está incompleto. Revisa el correo que recibiste.')}</p>
            </div>
          )}
        {/* Siempre una salida a login (evita el callejón sin token/enlace expirado). */}
        <p className="acc-alt"><a href={withBase('/login')}>{t('register.backToLogin', '← Volver al inicio de sesión')}</a></p>
      </div>
    </div>
  );
}

export function FrontlineActivateView() {
  return (
    <UserProvider>
      <ThemeProvider>
        <main className="acc-standalone">
          <ActivateContent />
        </main>
      </ThemeProvider>
    </UserProvider>
  );
}

export default FrontlineActivateView;
